import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";
import { formatDisplayMinuteOfDay } from "../../lib/format";
import {
  parseMinuteOfDay,
  windowEndMinute,
  windowFromStartEnd,
} from "../../lib/rules/ruleDerivation";
import type { Rule, RuleWindow } from "../../lib/rules/ruleTypes";
import {
  ruleWindowsEqual,
  validateSmartBlockWindow,
  type SmartBlockWindowValidationError,
} from "../../lib/rules/smartBlockWindow";
import { TimeField } from "./RuleEditorSheet";

const POPOVER_SPRING = { type: "spring" as const, stiffness: 520, damping: 36, mass: 0.7 };

type SmartBlockRuleWindowDialogProps = {
  open: boolean;
  rule: Rule | null;
  pending: boolean;
  onClose: () => void;
  onSave: (window: RuleWindow) => void;
};

function validationMessageKey(error: SmartBlockWindowValidationError): "smartBlockMinDurationError" | "smartBlockEndBeforeStartError" {
  if (error === "tooShort") return "smartBlockMinDurationError";
  return "smartBlockEndBeforeStartError";
}

export function SmartBlockRuleWindowDialog({
  open,
  rule,
  pending,
  onClose,
  onSave,
}: SmartBlockRuleWindowDialogProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const prefersReducedMotion = useReducedMotion();
  const dialogId = useId();
  const saveRef = useRef<HTMLButtonElement>(null);

  const [window, setWindow] = useState<RuleWindow | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    if (!open || !rule) return;
    setWindow(rule.window);
    setShowValidation(false);
  }, [open, rule]);

  const validation = useMemo(() => {
    if (!window) return null;
    return validateSmartBlockWindow(window);
  }, [window]);

  const unchanged = rule !== null && window !== null && ruleWindowsEqual(rule.window, window);
  const canSave = validation?.ok === true && !unchanged && !pending;

  const close = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  useEffect(() => {
    if (open) saveRef.current?.focus();
  }, [open]);

  const setStart = (value: string) => {
    if (!window) return;
    setWindow(windowFromStartEnd(parseMinuteOfDay(value), windowEndMinute(window)));
  };

  const setEnd = (value: string) => {
    if (!window) return;
    setWindow(windowFromStartEnd(window.startMinute, parseMinuteOfDay(value)));
  };

  const handleSave = () => {
    if (!window || !validation?.ok) {
      setShowValidation(true);
      return;
    }
    onSave(validation.window);
  };

  const centeredTransform = { x: "-50%", y: "-50%" } as const;
  const validationError =
    (showValidation || !unchanged) && validation && !validation.ok
      ? t.rules[validationMessageKey(validation.error)]
      : null;

  return typeof document !== "undefined"
    ? createPortal(
        <AnimatePresence>
          {open && rule && window ? (
            <>
              <div className="fixed inset-0 z-40" aria-hidden="true" onPointerDown={close} />
              <motion.div
                id={dialogId}
                role="dialog"
                aria-label={t.rules.smartBlockRuleWindowAria}
                className="liquid-glass-pill liquid-glass-pill-delete-confirm fixed left-1/2 top-1/2 z-50 w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-xl p-2.5"
                style={{ transformOrigin: "center center" }}
                initial={
                  prefersReducedMotion
                    ? { opacity: 0, ...centeredTransform }
                    : { opacity: 0, scale: 0.96, ...centeredTransform }
                }
                animate={{ opacity: 1, scale: 1, ...centeredTransform }}
                exit={
                  prefersReducedMotion
                    ? { opacity: 0, ...centeredTransform }
                    : { opacity: 0, scale: 0.96, ...centeredTransform }
                }
                transition={prefersReducedMotion ? { duration: 0 } : POPOVER_SPRING}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <div className="px-1 pb-2">
                  <h2 className="text-sm font-semibold text-[oklch(92%_0.02_150)]">
                    {t.rules.smartBlockRuleWindowTitle}
                  </h2>
                  <p className="mt-1 text-sm text-[oklch(74%_0.025_155)]">{t.rules.smartBlockRuleWindowBody}</p>
                </div>

                <div className="grid grid-cols-2 gap-2 px-1 pb-2">
                  <TimeField
                    label={t.rules.startLabel}
                    value={formatDisplayMinuteOfDay(window.startMinute)}
                    onChange={setStart}
                  />
                  <TimeField
                    label={t.rules.endLabel}
                    value={formatDisplayMinuteOfDay(windowEndMinute(window))}
                    onChange={setEnd}
                  />
                </div>

                {validationError ? (
                  <p className="px-1 pb-2 text-xs font-medium text-[oklch(68%_0.12_25)]" role="alert">
                    {validationError}
                  </p>
                ) : null}

                <div className="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    className="min-h-[44px] rounded-lg px-3 text-sm font-semibold text-[oklch(74%_0.025_155)] transition-colors active:bg-[oklch(24%_0.025_165)]"
                    disabled={pending}
                    onClick={close}
                  >
                    {t.common.cancel}
                  </button>
                  <button
                    ref={saveRef}
                    type="button"
                    className="min-h-[44px] rounded-lg bg-[oklch(72%_0.09_155)] px-3 text-sm font-semibold text-[oklch(12%_0.015_160)] transition-colors active:scale-[0.98] disabled:opacity-60"
                    disabled={!canSave}
                    onClick={handleSave}
                  >
                    {t.common.save}
                  </button>
                </div>
              </motion.div>
            </>
          ) : null}
        </AnimatePresence>,
        document.body,
      )
    : null;
}
